// libraries
import { roundToTwoDecimal } from '@makemydeal/dr-common-utils';
import type { IIncentives, ITermsIncentives } from '@makemydeal/dr-platform-shared';
import { manualIncentiveUtils } from '@makemydeal/dr-platform-shared';
import type {
    AppliedIncentives,
    IPencilOfferInputs,
    ManualIncentiveEntry,
    Offer,
    OfferType,
    SlimTermsIncentive,
    TermsIncentives
} from '@makemydeal/dr-platform-types';
import { ApplicationCodes, CASH } from '@makemydeal/dr-platform-types';
import { createSelector } from 'reselect';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import { reducerConfig } from '../reducers/reducerConfig';

// utils
import {
    isUserCustomIncentive,
    isProgramOverrideIncentive,
    isDealerCash,
    isNotDealerCash,
    isProgramIncentivesRestorationEnabled,
    isProgramStandardIncentive,
    transformToManualIncentive,
    isFrozenProgramRow,
    isAppliedIncentiveDisplayFixesEnabled,
    isGridIncentiveManagementEnabled,
    isUpqRebateLedgerInEffect,
    isEditedProgramIncentiveDedupeEnabled,
    isHonorDealerStandardExclusionEnabled,
    isManualRebateForRequest,
    isCashRebateLedgerSeedEnabled
} from '../utils/incentives';
import { parseTermId } from '../utils/termIdParser';
import { getDealerNode } from './dealerSelectors';
import { getExcludeStdCashIncsByOfferType, isCalculatingPayment } from './paymentSelectors';
import * as helpers from './helpers';
import {
    getCurrentOfferType,
    getHonorOverrides,
    getIncentiveById,
    getIncentives,
    getIncentivesTotalOverrideByOfferType,
    getIsUserProgramQuotes,
    getIsUserProgramQuotesSinceLoad,
    getManualIncentivesByOfferType,
    getOfferDetails,
    getSelectedIncentivesByOfferType,
    getSelectedTerm,
    getSelectedTermByOfferType,
    getTerm,
    getTermIdForOfferType
} from './offerNodeSelectors';
import {
    buildConditionalIncentiveKey,
    buildNonConditionalIncentiveKey,
    getOverriddenIncentiveCodes,
    parseIncentiveId,
    resolveIncentiveKey,
    resolveIncentiveNumericId
} from '../utils/incentives';

// interfaces/types
import type {
    AIOIncentives,
    OfferIncentiveWithAmount,
    LedgerAwareManualIncentives,
    RowProvenance,
    TermsAppliedIncentives,
    UserRowSource
} from '../types/commonTypes';
import { isEmpty } from '../utils/common';
import { getTermFromOffer, getTermMonthByOfferTypeFromOffer } from './slices/offerSliceSelectors';

// Shared and frozen so a missing catalog resolves to the same reference on every call - callers
// (e.g. dash-store's makeTermIncentivesSelector) sit inside a createSelector and rely on that
// stability to avoid recomputing on every store notification while the catalog is absent. Frozen at
// runtime (not just typed readonly) because this is a published package - an external caller
// mutating what it read as an empty array would otherwise corrupt the shared instance for every
// other caller and offer type for the life of the page. `as any` sidesteps `Readonly<T>` not being
// assignable to the mutable `any[]`/`IIncentives` these functions already return; the object is
// still genuinely frozen at runtime.
const EMPTY_INCENTIVES_NODE: IIncentives = Object.freeze({}) as any;
const EMPTY_INCENTIVES_LIST: any[] = Object.freeze([]) as any;

export const getCashIncentivesNode = (offer: Offer): IIncentives => {
    const penciledInputs = getOfferDetails(offer, CASH);

    return penciledInputs?.incentives || EMPTY_INCENTIVES_NODE;
};
export const getCashConditionalsIncentives = (offer: Offer): any[] => {
    const incentives = getCashIncentivesNode(offer);

    return incentives.conditionals || EMPTY_INCENTIVES_LIST;
};

export const getCashNonConditionalsIncentives = (offer: Offer): any[] => {
    const incentives = getCashIncentivesNode(offer);

    return incentives.nonConditionals || EMPTY_INCENTIVES_LIST;
};

export const getCashIncentivesContext = (offer: Offer): { compatibleIncentiveIds?: number[] } | undefined => {
    const penciledInputs = getOfferDetails(offer, CASH) as { incentivesContext?: { compatibleIncentiveIds?: number[] } };

    return penciledInputs?.incentivesContext;
};

export const getCashAppliedIncentivesNode = (offer: Offer): AppliedIncentives => {
    const penciledInputs = getOfferDetails(offer, CASH);

    return penciledInputs?.appliedIncentives || ({} as AppliedIncentives);
};

export const getCashAppliedConditionalsIncentives = (offer: Offer): string[] => {
    const appliedIncentives = getCashAppliedIncentivesNode(offer);

    return appliedIncentives.conditionals || [];
};

export const getCashAppliedNonConditionalsIncentives = (offer: Offer): string[] => {
    const appliedIncentives = getCashAppliedIncentivesNode(offer);

    return appliedIncentives.nonConditionals || [];
};

export const getCashSelectedIncentives = (offer: Offer): any[] => {
    const penciledInputs = getOfferDetails(offer, CASH);
    return penciledInputs.selectedIncentives || [];
};

export function getNonConditionalIncentiveFromTerm(state: any, termId: any): SlimTermsIncentive[] {
    const term = getTerm(state, termId);
    return term?.incentives?.nonConditionals || [];
}

export function getNonConditionalAppliedIncentiveFromTerm(state: any, termId: any): string[] {
    const term = getTerm(state, termId);
    return term?.appliedIncentives?.nonConditionals || [];
}

export function getConditionalIncentiveFromTerm(state: any, termId: any): SlimTermsIncentive[] {
    const term = getTerm(state, termId);
    return term?.incentives?.conditionals;
}

export function getConditionalIncentiveFromOfferTerm(offer: any, termId: any): SlimTermsIncentive[] {
    const paymentTerm = getTermFromOffer(offer, termId);
    return paymentTerm.incentives?.conditionals;
}

const resolveCashIncentiveAmount = (
    catalogAmount: number | null | undefined,
    recordAmount?: number | null
): number | null | undefined => catalogAmount ?? recordAmount ?? catalogAmount;

const withResolvedCashAmount = (offer: Offer, row: { id: string; amount?: number | null }): SlimTermsIncentive => {
    const incentiveRecord = getIncentiveById(offer, row.id);
    return { ...incentiveRecord, amount: resolveCashIncentiveAmount(row.amount, incentiveRecord?.amount) } as SlimTermsIncentive;
};

// Cash Non Conditional Incentives
export const getCashNonConditionalIncentives = (offer: Offer): SlimTermsIncentive[] => {
    const cashNonConditionalIncentives = getCashNonConditionalsIncentives(offer);
    const cashAppliedNonConditionalIncentives = getCashAppliedNonConditionalsIncentives(offer);

    const incentiveResult: SlimTermsIncentive[] = [];
    cashNonConditionalIncentives.forEach((incentive) => {
        if (incentive.id && cashAppliedNonConditionalIncentives.indexOf(incentive.id) >= 0) {
            incentiveResult.push(withResolvedCashAmount(offer, incentive));
        }
    });
    return incentiveResult;
};
// Cash Conditional Incentives
export const getCashConditionalIncentives = (offer: Offer): SlimTermsIncentive[] => {
    const cashConditionalIncentives = getCashConditionalsIncentives(offer);

    const incentiveResult: SlimTermsIncentive[] = [];

    cashConditionalIncentives.forEach((condIncentive) => {
        if (condIncentive?.id) {
            incentiveResult.push(withResolvedCashAmount(offer, condIncentive));
        }
    });
    return incentiveResult;
};

export const getCompatibleIncentiveIdsForTerm = (state: any, termId: any): Set<number> | null => {
    const term = getTerm(state, termId) as
        | { compatibleIncentiveIds?: number[]; incentivesContext?: { compatibleIncentiveIds?: number[] } }
        | undefined;
    const direct = term?.compatibleIncentiveIds;
    const compatibleIds = Array.isArray(direct) && direct.length > 0 ? direct : term?.incentivesContext?.compatibleIncentiveIds;
    return Array.isArray(compatibleIds) && compatibleIds.length > 0 ? new Set<number>(compatibleIds) : null;
};

const isCashOfferType = (state: any, offerType: OfferType | undefined): boolean =>
    (offerType || getCurrentOfferType(state)) === CASH;

export const shouldHonorDealerStandardExclusion = (state: any, offerType: OfferType | undefined): boolean =>
    isHonorDealerStandardExclusionEnabled(state) &&
    isProgramIncentivesRestorationEnabled(state) &&
    reducerConfig?.sourceApplication !== ApplicationCodes.MV &&
    Boolean(getExcludeStdCashIncsByOfferType(getDealerNode(state), offerType as OfferType));

const resolveTermOfferType = (state: any, termId: string | undefined, offerType: OfferType | undefined): OfferType =>
    (parseTermId(termId).offerType as OfferType | undefined) ?? offerType ?? getCurrentOfferType(state);

const shouldHonorDealerStandardExclusionForTerm = (
    state: any,
    termId: string | undefined,
    offerType: OfferType | undefined
): boolean =>
    isHonorDealerStandardExclusionEnabled(state) &&
    shouldHonorDealerStandardExclusion(state, resolveTermOfferType(state, termId, offerType));

const getCatalogIncentivesForOfferType = (
    state: any,
    offerType: OfferType | undefined,
    kind: 'conditionals' | 'nonConditionals'
): SlimTermsIncentive[] => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    const ids: Array<string | number> = getIncentives(state)?.byOfferType?.[resolvedOfferType]?.[kind] ?? [];
    const buildKey = kind === 'conditionals' ? buildConditionalIncentiveKey : buildNonConditionalIncentiveKey;
    return ids.map((rawId) => {
        const id = typeof rawId === 'number' ? buildKey(resolvedOfferType, rawId) : rawId;
        return { id } as SlimTermsIncentive;
    });
};

export function getCatalogNonConditionalIdsByOfferType(state: any, offerType?: OfferType): number[] {
    return getCatalogIncentivesForOfferType(state, offerType, 'nonConditionals')
        .map((incentive) => parseIncentiveId(incentive.id))
        .filter((id) => !Number.isNaN(id));
}

const getPenciledKeysWithPrefix = (
    state: any,
    offerType: OfferType | undefined,
    prefixFor: (resolved: string) => string
): string[] => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    const prefix = prefixFor(resolvedOfferType);
    const selected = (getSelectedIncentivesByOfferType(state, resolvedOfferType) ?? []) as Array<string | number>;
    return selected.filter((key): key is string => typeof key === 'string' && key.startsWith(prefix));
};

const getPenciledNonConditionalKeys = (state: any, offerType?: OfferType): string[] =>
    getPenciledKeysWithPrefix(state, offerType, (resolved) => `nonconditional_${resolved}_`);

const getPenciledConditionalKeys = (state: any, offerType?: OfferType): string[] =>
    getPenciledKeysWithPrefix(state, offerType, (resolved) => `conditional_${resolved}_`);

export function getSelectedNonConditionalIdsFromPenciledInputs(state: any, offerType?: OfferType): number[] {
    return getPenciledNonConditionalKeys(state, offerType)
        .map((key) => parseIncentiveId(key))
        .filter((id) => !Number.isNaN(id));
}

/**
 * Conditional-only counterpart to getSelectedNonConditionalIdsFromPenciledInputs. Callers that treat
 * a recorded selection as conditional candidates must use this rather than the namespace-flattened
 * getSelectedIncentiveIdsFromPenciledInputs, which cannot tell a recorded standard from a conditional.
 */
export function getSelectedConditionalIdsFromPenciledInputs(state: any, offerType?: OfferType): number[] {
    return helpers.getMappedIncentives(getIncentives(state)?.mappings ?? {}, getPenciledConditionalKeys(state, offerType));
}

export function hasRecordedIncentiveSelection(state: any, offerType?: OfferType): boolean {
    return hasRecordedNonConditionalSelection(state, offerType) || hasRecordedConditionalSelection(state, offerType);
}

export function hasRecordedNonConditionalSelection(state: any, offerType?: OfferType): boolean {
    return getPenciledNonConditionalKeys(state, offerType).length > 0;
}

export function hasRecordedConditionalSelection(state: any, offerType?: OfferType): boolean {
    return getPenciledConditionalKeys(state, offerType).length > 0;
}

export function getEditedIncentiveIds(state: any, offerType: OfferType): Set<number> {
    const liveRows = (getManualIncentivesByOfferType(state, offerType) ?? []).filter((row) => !row.deleted);
    const programBackedCodes = new Set(liveRows.filter((row) => !row.manual).map(resolveIncentiveNumericId));
    return new Set([...getOverriddenIncentiveCodes(liveRows)].filter((code) => !programBackedCodes.has(code)));
}

const getGatedAppliedIdsForTerm = (state: any, termId: unknown): Set<number> | null => {
    const termOfferType = String(termId ?? '').split('_')[0];
    return isGridIncentiveManagementEnabled(state, termOfferType)
        ? new Set((getTerm(state, termId as string)?.appliedIncentiveIds as number[] | undefined) ?? [])
        : null;
};

const resolveRowAmount = (row: SlimTermsIncentive, isApplied: boolean, fallback: () => number): number =>
    isApplied ? row.amount ?? fallback() : row.amount || fallback();

export const buildNonConditionalDisplayRow = (
    state: any,
    incentive: SlimTermsIncentive,
    context: {
        months?: number;
        restorationEnabled: boolean;
        compatibleIds: Set<number> | null;
        appliedNonConditionalIncentives: string[];
        appliedIds?: Set<number> | null;
    }
): (SlimTermsIncentive & { pdId?: number }) | undefined => {
    const { months, restorationEnabled, compatibleIds, appliedNonConditionalIncentives, appliedIds } = context;
    const incentiveId = incentive.id;
    const pdId = incentiveId ? parseIncentiveId(incentiveId) : NaN;
    const isApplied = Boolean(appliedIds?.has(pdId));
    if (compatibleIds && !compatibleIds.has(pdId) && !isApplied) {
        return undefined;
    }
    const rawIncentive = getIncentiveById(state, incentiveId);
    const amount = resolveRowAmount(incentive, isApplied, () => helpers.transformIncentiveAmount(months as number, rawIncentive));
    if (restorationEnabled) {
        return incentiveId && (amount || isApplied) ? { ...rawIncentive, ...incentive, id: incentiveId, amount, pdId } : undefined;
    }
    return incentiveId && appliedNonConditionalIncentives.indexOf(incentiveId) >= 0 ? { ...rawIncentive, amount } : undefined;
};

/**
 * Returns the non-conditional incentives to display for a term: the applied ones resolved with
 * their amounts (OFF path), or every term non-conditional that has an amount (restoration ON).
 * @param state - Redux state.
 * @param termId - Term id to read incentives from.
 * @param offerType - Offer type used to resolve the selected term (months).
 * @returns Non-conditional incentives to display.
 */
export function getNonConditionalIncentivesToDisplayForTermId(state: any, termId: any, offerType?: OfferType) {
    const termNonConditionals = getNonConditionalIncentiveFromTerm(state, termId);
    const appliedNonConditionalIncentives = getNonConditionalAppliedIncentiveFromTerm(state, termId);
    const restorationEnabled =
        isProgramIncentivesRestorationEnabled(state) && !shouldHonorDealerStandardExclusionForTerm(state, termId, offerType);
    const nonConditionalIncentivesToDisplay: Array<SlimTermsIncentive & { pdId?: number }> = [];

    const compatibleIds = restorationEnabled ? getCompatibleIncentiveIdsForTerm(state, termId) : null;

    const nonConditionalIncentives =
        restorationEnabled && !termNonConditionals?.length && compatibleIds && !isCashOfferType(state, offerType)
            ? getCatalogIncentivesForOfferType(state, offerType, 'nonConditionals')
            : termNonConditionals;

    const months = getSelectedTerm(state, offerType)?.months;
    const appliedIds = getGatedAppliedIdsForTerm(state, termId);

    nonConditionalIncentives.forEach((incentive) => {
        const row = buildNonConditionalDisplayRow(state, incentive, {
            months,
            restorationEnabled,
            compatibleIds,
            appliedNonConditionalIncentives,
            appliedIds
        });
        if (row) {
            nonConditionalIncentivesToDisplay.push(row);
        }
    });

    if (!restorationEnabled && appliedNonConditionalIncentives.length) {
        appendAppliedNonConditionalsMissingFromTerm(
            state,
            offerType,
            nonConditionalIncentives,
            appliedNonConditionalIncentives,
            nonConditionalIncentivesToDisplay
        );
    }

    return nonConditionalIncentivesToDisplay;
}

/**
 * Surfaces applied non-conditional incentives that are absent from the term's available list
 * (e.g. standard incentives Payment Services applies but `UPDATE_TERM_AVAILABLE_INCENTIVES` does
 * not echo), resolving each from the incentive catalog so the manual incentives card and totals
 * still reflect them on the OFF path. Mutates `nonConditionalIncentivesToDisplay` in place.
 * @param state - Redux state.
 * @param offerType - Offer type used to resolve the selected term (months).
 * @param availableNonConditionals - The term's available non-conditional incentives.
 * @param appliedNonConditionalIncentives - Applied non-conditional incentive ids on the term.
 * @param nonConditionalIncentivesToDisplay - Accumulator appended with any missing applied incentives.
 */
function appendAppliedNonConditionalsMissingFromTerm(
    state: any,
    offerType: OfferType | undefined,
    availableNonConditionals: SlimTermsIncentive[],
    appliedNonConditionalIncentives: string[],
    nonConditionalIncentivesToDisplay: Array<SlimTermsIncentive & { pdId?: number }>
): void {
    const availableIds = new Set(availableNonConditionals.map((incentive) => incentive.id));
    const months = getSelectedTerm(state, offerType)?.months;
    const appended = new Set<string>();

    appliedNonConditionalIncentives.forEach((appliedId) => {
        if (availableIds.has(appliedId) || appended.has(appliedId)) return;
        const rawIncentive = getIncentiveById(state, appliedId);
        if (!rawIncentive || Object.keys(rawIncentive).length === 0) return;
        const amount = helpers.transformIncentiveAmount(months, rawIncentive);
        nonConditionalIncentivesToDisplay.push({ ...rawIncentive, id: appliedId, amount });
        appended.add(appliedId);
    });
}

/**
 * Get incentives transformed for AIO
 * @param state
 */
export const getAppliedIncentivesMappedForAIO = (state: any, offerType?: OfferType): AIOIncentives => {
    const incentives = offerType ? getAppliedIncentivesByOfferType(state, offerType) : getAppliedIncentives(state); // in SP, these will exclude dealer cash regardless of the filter.
    if (!incentives.length) {
        return [];
    }
    return incentives.map((incentive: ManualIncentiveEntry) => {
        return {
            incentiveName: incentive.name,
            incentiveCode: incentive.code || 0,
            incentiveDescription: incentive.description || incentive.name,
            incentiveAmount: incentive.amount,
            incentiveProgramNumber: incentive.program as string,
            incentiveIsStandard: incentive.isStandardIncentive,
            incentiveIsManual: incentive.manual,
            incentiveType: incentive.type,
            incentiveCategory: incentive.conditionCategory,
            incentiveMethod: incentive.financeMethod,
            incentiveFundingSource: incentive.fundingSource,
            incentiveTermRebates: incentive.termRebates,
            incentiveProgramType: incentive.programType,
            incentiveStartDate: incentive.startDate,
            incentiveEndDate: incentive.endDate,
            incentiveExclusions: incentive.exclusions
        };
    });
};

/**
 * Returns the conditional incentives to display for a term, each resolved with its amount.
 * @param state - Redux state.
 * @param termId - Term id to read incentives from.
 * @param offerType - Offer type used to resolve the selected term (months).
 * @returns Conditional incentives to display.
 */
export function getConditionalIncentivesToDisplayForTermId(state: any, termId: any, offerType?: OfferType) {
    const termConditionals = getConditionalIncentiveFromTerm(state, termId) || [];
    const restorationEnabled = isProgramIncentivesRestorationEnabled(state);
    const conditionalIncentivesToDisplay: OfferReduxBaseState[] = [];

    const appliedIds = getGatedAppliedIdsForTerm(state, termId);
    const isAppliedRow = (incentive: SlimTermsIncentive): boolean =>
        Boolean(incentive.id && appliedIds?.has(parseIncentiveId(incentive.id)));
    let conditionalIncentives = termConditionals;
    if (restorationEnabled && !termConditionals.length && !isCashOfferType(state, offerType)) {
        const compatibleIds = getCompatibleIncentiveIdsForTerm(state, termId);
        conditionalIncentives = compatibleIds
            ? getCatalogIncentivesForOfferType(state, offerType, 'conditionals').filter(
                  (incentive) => incentive.id && (compatibleIds.has(parseIncentiveId(incentive.id)) || isAppliedRow(incentive))
              )
            : [];
    }

    if (conditionalIncentives && termId) {
        const selectedTerm = getSelectedTerm(state, offerType);
        const months = selectedTerm?.months;
        if (months) {
            conditionalIncentives.forEach((condIncentive) => {
                const rawIncentive = getIncentiveById(state, condIncentive.id);
                const isApplied = isAppliedRow(condIncentive);
                const amount = resolveRowAmount(condIncentive, isApplied, () =>
                    helpers.transformIncentiveAmount(months, rawIncentive)
                );
                if (condIncentive?.id && (amount || isApplied)) {
                    const base = restorationEnabled
                        ? { ...rawIncentive, ...condIncentive, pdId: parseIncentiveId(condIncentive.id) }
                        : rawIncentive;
                    conditionalIncentivesToDisplay.push({ ...base, id: condIncentive.id, amount });
                }
            });
        }
    }

    return conditionalIncentivesToDisplay;
}

const getCashOfferSource = (state: any) => (isCashRebateLedgerSeedEnabled(state) ? state : state.offer);

export function getSelectedConditionalIncentivesToDisplay(state: any, offerType?: OfferType) {
    const selectedOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, selectedOfferType);
    const restorationEnabled = isProgramIncentivesRestorationEnabled(state);

    // Cash has no terms; keep sourcing the selection from penciled inputs.
    if (selectedOfferType === 'cash') {
        const cashConditionalIncentives = getCashConditionalIncentives(getCashOfferSource(state));
        const selectedConditionalIncentiveIds = getSelectedIncentivesByOfferType(state, selectedOfferType);
        if (isEmpty(selectedConditionalIncentiveIds)) return [];
        return cashConditionalIncentives.filter((incentive) => selectedConditionalIncentiveIds.includes(incentive.id));
    }

    const conditionalIncentives = getConditionalIncentivesToDisplayForTermId(state, offerDetails.term, offerType);

    // Restoration ON: the applied set lives on the term, not in penciled selectedIncentives.
    if (restorationEnabled) {
        const appliedIncentiveIds = getAppliedIncentiveIdsForSelectedTerm(state, selectedOfferType);
        if (isEmpty(appliedIncentiveIds)) return [];
        return conditionalIncentives.filter((incentive) => appliedIncentiveIds.includes(incentive.pdId));
    }

    const selectedConditionalIncentiveIds = getSelectedIncentivesByOfferType(state, selectedOfferType);
    if (isEmpty(selectedConditionalIncentiveIds)) return [];
    return conditionalIncentives.filter((incentive) => selectedConditionalIncentiveIds.includes(incentive.id));
}

/**
 * Get original incentives
 * @param state
 */
export const getOriginalIncentives = (state: any, offerType?: OfferType): ManualIncentiveEntry[] => {
    const nonConditionals = getSelectedNonConditionalIncentivesToDisplay(state, offerType);
    const conditionals = getSelectedConditionalIncentivesToDisplay(state, offerType);
    return [...nonConditionals, ...conditionals];
};

/**
 * Entry Point (use this one) 2023-06
 * Get all incentives (new manual rebates or program incentives)
 * Checks state (offer.penciledInputs.byOfferType.manualIncentives or offer.incentives w/ offer.penciledInputs.byOffertype.type.selectedIncentives)
 * @param state
 * @returns
 */
export function getAppliedIncentives(state: any) {
    const offerType = getCurrentOfferType(state);
    return getAppliedIncentivesByOfferType(state, offerType);
}

export const getAppliedNonZeroIncentives = (state: any) => {
    return getAppliedIncentives(state)?.filter((incentive) => incentive?.amount > 0);
};

export const getAppliedIncentivesIds = (state: any): string | undefined => {
    return getAppliedNonZeroIncentives(state)
        ?.map((incentive) => incentive?.code)
        .join(',');
};

export const getAppliedIncentivesTypes = (state: any): string | undefined => {
    return getAppliedNonZeroIncentives(state)
        ?.map((incentive) => incentive?.type)
        .join(',');
};

export const getAppliedIncentivesValues = (state: any): string | undefined => {
    return getAppliedNonZeroIncentives(state)
        ?.map((incentive) => incentive?.amount)
        .join(',');
};

export const getAppliedIncentivesNames = (state: any): string | undefined => {
    return getAppliedNonZeroIncentives(state)
        ?.map((incentive) => incentive?.name)
        .join(',');
};

/**
 * Entry Point (use this one) 2023-06
 * Get all incentives (new manual rebates or program incentives)
 * Checks state (offer.penciledInputs.byOfferType.manualIncentives or offer.incentives w/ offer.penciledInputs.byOffertype.type.selectedIncentives)
 * @param state
 * @param offerType
 * @returns
 */
const getTermIncentiveKeys = (state: any, offerType: OfferType): Set<string> => {
    if (isCashOfferType(state, offerType)) {
        return new Set();
    }
    const termId = getTermIdForOfferType(state.offer, offerType);
    const termIncentives = [
        ...(getConditionalIncentiveFromTerm(state, termId) ?? []),
        ...getNonConditionalIncentiveFromTerm(state, termId)
    ];
    return new Set(termIncentives.map(resolveIncentiveKey).filter((key): key is string => key != null));
};

const getCompatibleIdsForOfferTypeTerm = (state: any, offerType: OfferType): Set<number> | null => {
    const termId = getTermIdForOfferType(state.offer, offerType);
    return termId != null ? getCompatibleIncentiveIdsForTerm(state, termId) : null;
};

const isProgramSourcedOutsideCompatibleSet = (incentive: ManualIncentiveEntry, compatibleIds: Set<number> | null): boolean => {
    if (incentive.manual !== false || compatibleIds == null) {
        return false;
    }
    const compatibleId = resolveIncentiveNumericId(incentive);
    return compatibleId != null && !compatibleIds.has(compatibleId);
};

const getSelectedProgramIncentiveKeys = (state: any, offerType: OfferType): Set<string> => {
    const selected = getSelectedIncentivesByOfferType(state, offerType) as Array<string | number>;
    return new Set(
        selected
            .map((key) => parseIncentiveId(String(key)))
            .filter((id) => !Number.isNaN(id))
            .map((id) => String(id))
    );
};

// Stricter than `isProgramStandardIncentive`, which accepts `manual !== true`: this decides whether a
// persisted row is the program's own echo and may be dropped from the manual list, so a row of unknown
// provenance stays in the user's list instead of being folded into the program rows.
const isProgramSourcedRow = (incentive: ManualIncentiveEntry): boolean =>
    incentive.manual === false && !isProgramStandardIncentive(incentive);

const resolveRowProvenance = (source: UserRowSource): RowProvenance =>
    isProgramStandardIncentive(source) ? { manual: false, isStandardIncentive: true } : { manual: true };

const withUserRowIdentity = (mapped: ManualIncentiveEntry, source: UserRowSource): ManualIncentiveEntry => ({
    ...mapped,
    ...resolveRowProvenance(source),
    ...(source.originalAmount != null && { originalAmount: source.originalAmount }),
    ...(source._editId != null && { _editId: source._editId })
});

const belongsToManualList = (incentive: ManualIncentiveEntry, nonManualKeys: Set<string>): boolean => {
    const key = resolveIncentiveKey(incentive);
    return key == null || !nonManualKeys.has(key) || isUserCustomIncentive(incentive);
};

const getUserEnteredRowKeys = (rows: ManualIncentiveEntry[]): Set<string> =>
    new Set(
        rows
            .filter(isUserCustomIncentive)
            .map(resolveIncentiveKey)
            .filter((key): key is string => key != null)
    );

const getProgramOverrideRowKeys = (rows: ManualIncentiveEntry[]): Set<string> =>
    new Set(
        rows
            .filter(isProgramOverrideIncentive)
            .map(resolveIncentiveKey)
            .filter((key): key is string => key != null)
    );

const isCorroboratedProgramIncentive = (state: any, offerType: OfferType, incentive: ManualIncentiveEntry): boolean => {
    const catalogEntry = getIncentiveById(state, (incentive as { id?: string }).id as string) as Record<string, unknown>;
    if (Object.keys(catalogEntry).length > 0 && catalogEntry.manual !== true) {
        return true;
    }
    const compatibleIds = getCompatibleIncentiveIdsForTerm(state, getOfferDetails(state, offerType).term);
    return compatibleIds != null && compatibleIds.has(resolveIncentiveNumericId(incentive) as number);
};

const withoutUserRowEchoes = (
    state: any,
    offerType: OfferType,
    catalogIncentives: ManualIncentiveEntry[],
    userEnteredKeys: Set<string>
): ManualIncentiveEntry[] => {
    if (userEnteredKeys.size === 0) {
        return catalogIncentives;
    }
    return catalogIncentives.filter(
        (incentive) =>
            !userEnteredKeys.has(resolveIncentiveKey(incentive) as string) ||
            isCorroboratedProgramIncentive(state, offerType, incentive)
    );
};

const getOverriddenProgramKeys = (state: any, rawManual: ManualIncentiveEntry[]): Set<string> =>
    isEditedProgramIncentiveDedupeEnabled(state) ? getProgramOverrideRowKeys(rawManual) : new Set();

export const getOverriddenProgramIds = (state: any, offerType: OfferType): Set<number> => {
    if (!isEditedProgramIncentiveDedupeEnabled(state) || !isProgramIncentivesRestorationEnabled(state)) return new Set();
    const rawManual = getManualIncentivesByOfferType(state, offerType) ?? [];
    return new Set([...getOverriddenProgramKeys(state, rawManual)].map(Number).filter((id) => !Number.isNaN(id)));
};

const isOverriddenProgramRow = (incentive: ManualIncentiveEntry, overriddenKeys: Set<string>): boolean =>
    !isProgramStandardIncentive(incentive) && overriddenKeys.has(resolveIncentiveKey(incentive) as string);

const withoutOverriddenProgramRows = (programRows: ManualIncentiveEntry[], overriddenKeys: Set<string>): ManualIncentiveEntry[] =>
    overriddenKeys.size === 0 ? programRows : programRows.filter((incentive) => !isOverriddenProgramRow(incentive, overriddenKeys));

/**
 * The program-sourced half of the applied list, derived from the selected term record. Shared with the
 * freeze so it stores exactly what the PQ read resolved - deriving the rows separately would let the frozen
 * set disagree with what the operator saw when they applied the program.
 */
export const getProgramSourcedAppliedIncentives = (
    state: any,
    offerType: OfferType,
    rawManual: ManualIncentiveEntry[]
): ManualIncentiveEntry[] => {
    // For cash, getOriginalIncentives can return incorrect pre-sync rows with no resolvable
    // code/pdId/id (e.g. before the program orchestrator has responded) - such a row can't be
    // deduped against the manual bag below, producing a duplicate. Drop unresolvable rows here.
    const originalIncentives = getOriginalIncentives(state, offerType);
    const catalogIncentives =
        offerType === CASH ? originalIncentives.filter((incentive) => resolveIncentiveKey(incentive) != null) : originalIncentives;
    const programIncentives = withoutUserRowEchoes(state, offerType, catalogIncentives, getUserEnteredRowKeys(rawManual)).map(
        transformToManualIncentive
    );
    const seenKeys = new Set<string>();
    return programIncentives.reduce((acc: ManualIncentiveEntry[], incentive) => {
        const key = resolveIncentiveKey(incentive);
        if (key != null) {
            if (seenKeys.has(key)) {
                return acc;
            }
            seenKeys.add(key);
        }
        acc.push(incentive);
        return acc;
    }, []);
};

/** The rows a good `/program-quotes` response freezes into the penciled bag, marked as program-sourced. */
export const getProgramRebatesToFreeze = (state: any, offerType: OfferType): ManualIncentiveEntry[] => {
    const rawManual = getManualIncentivesByOfferType(state, offerType) ?? [];

    // Not re-run through `transformToBaseIncentive`: these rows already went through
    // `transformToManualIncentive`, and transforming again flattens them to a user shape (`manual: true`),
    // which would make a vehicle switch or program re-apply keep a superseded rebate.
    return getProgramSourcedAppliedIncentives(state, offerType, rawManual).map((incentive) => ({
        ...incentive,
        frozenFromProgram: true
    }));
};

/**
 * True while the penciled bag - not the selected term - owns the applied rebate: the ledger toggle is on,
 * the offer type is priced through /user-program-quotes, and the bag either holds frozen rows or has been
 * marked owned (an operator emptied it). Until then the term-derived behaviour is unchanged.
 */
const holdsLastPricedRebates = (state: any, offerType: OfferType): boolean =>
    isGridIncentiveManagementEnabled(state, offerType) && isCalculatingPayment(state);

const isUpqRebateLedgerActive = (state: any, offerType: OfferType, forUpqRequest = false): boolean => {
    if (!isUpqRebateLedgerInEffect(state, offerType)) return false;
    if (!forUpqRequest && !getIsUserProgramQuotes(state, offerType) && !holdsLastPricedRebates(state, offerType)) return false;
    const rawManual = getManualIncentivesByOfferType(state, offerType) ?? [];
    const isBagLedgerOwned = Boolean(
        (getOfferDetails(state, offerType)?.manualIncentives as LedgerAwareManualIncentives | undefined)?.ledgerOwned
    );
    return rawManual.some(isFrozenProgramRow) || isBagLedgerOwned;
};

export function getAppliedIncentivesByOfferType(state: any, offerType: OfferType, forUpqRequest = false) {
    const { updateManualIncentivesMV } = state.featureToggles || {};
    const toBase = (incentive: ManualIncentiveEntry) =>
        manualIncentiveUtils.transformToBaseIncentive(incentive, updateManualIncentivesMV);

    if (isProgramIncentivesRestorationEnabled(state)) {
        const rawManual = getManualIncentivesByOfferType(state, offerType) ?? [];
        // On /user-program-quotes the program half comes from the frozen rows in the penciled bag, which is
        // term-independent, so the Rebates row and `overrides.manualIncentives` cannot disagree. Deriving it
        // from the term loses the rebate whenever the selected term id does not resolve, and reports 0 when
        // it resolves to a barren payment-grid row.
        //
        // Only once the bag is populated OR owned: a deal that has never been frozen must keep deriving from
        // the term, while an owned-but-empty bag means the operator removed everything and must not.
        const frozenRows = rawManual.filter(isFrozenProgramRow);
        const mergedProgram = withoutOverriddenProgramRows(
            isUpqRebateLedgerActive(state, offerType, forUpqRequest)
                ? frozenRows
                : getProgramSourcedAppliedIncentives(state, offerType, rawManual),
            getOverriddenProgramKeys(state, rawManual)
        );
        const programKeys = mergedProgram.map(resolveIncentiveKey).filter((key): key is string => key != null);
        const nonManualKeys = new Set<string>([
            ...programKeys,
            ...getTermIncentiveKeys(state, offerType),
            ...getSelectedProgramIncentiveKeys(state, offerType)
        ]);
        const compatibleIds = getCompatibleIdsForOfferTypeTerm(state, offerType);
        const manualOnly = rawManual
            .filter((incentive) => !isProgramSourcedRow(incentive))
            // A frozen row is never an operator entry, so it must never reach the manual list - it would
            // otherwise pass these filters and be counted twice alongside the program half.
            .filter((incentive) => !isFrozenProgramRow(incentive))
            .filter((incentive) => belongsToManualList(incentive, nonManualKeys))
            .filter((incentive) => !isProgramSourcedOutsideCompatibleSet(incentive, compatibleIds))
            .map((incentive) => withUserRowIdentity(toBase(incentive), incentive));
        return [...mergedProgram, ...manualOnly];
    }

    const penciledIncentives = getManualIncentivesByOfferType(state, offerType);
    return penciledIncentives
        ? penciledIncentives.map(toBase)
        : getOriginalIncentives(state, offerType).map(transformToManualIncentive);
}

/**
 * Internal: Selected rebates, filtered. Use `isDealerCash` or `isNotDealerCash` as filters
 * @param state
 * @param offerType
 * @param filter Array.filter check; return true for items to keep.
 */
/**
 * Returns the applied incentives for an offer type, optionally narrowed by a predicate.
 * @param state - Redux state.
 * @param offerType - Offer type whose applied incentives are returned.
 * @param filter - Optional predicate; return true for items to keep.
 * @returns Applied incentives, filtered when a predicate is supplied.
 */
export const getAppliedIncentivesWithFilter = (
    state: any,
    offerType: OfferType,
    filter?: (arg: ManualIncentiveEntry) => boolean,
    forUpqRequest = false
): ManualIncentiveEntry[] => {
    const list = getAppliedIncentivesByOfferType(state, offerType, forUpqRequest);
    if (filter != null) return list.filter(filter);
    return list;
};

/**
 * Resolves the cash a row contributes for the deal's term.
 *
 * Kept as a named helper so the display list and `getAppliedIncentivesTotalWithFilter` resolve amounts
 * identically; they feed the screen, the payment request's `rebates` and the PDF respectively.
 */
const resolveAmountForTerm = (termsByMonth: number, row: ManualIncentiveEntry, keepAppliedZero: boolean): number =>
    keepAppliedZero && row.amount === 0 && !isUserCustomIncentive(row)
        ? 0
        : Number(helpers.transformIncentiveAmount(termsByMonth, row)) || 0;

/**
 * Internal: Calculate total for incentives with optional filter.
 * @param state
 * @param offerType
 * @param filter Compare function; return true for items to keep
 * @returns
 */
function getAppliedIncentivesTotalWithFilter(
    state: any,
    offerType: OfferType,
    filter?: (arg: ManualIncentiveEntry) => boolean,
    forUpqRequest = false
) {
    const termsByMonth = getTermMonthByOfferTypeFromOffer(state.offer, offerType);
    const keepAppliedZero = isGridIncentiveManagementEnabled(state, offerType);
    return roundToTwoDecimal(
        getAppliedIncentivesWithFilter(state, offerType, filter, forUpqRequest).reduce(
            (total, current) => total + resolveAmountForTerm(termsByMonth as number, current, keepAppliedZero),
            0
        )
    );
}

/**
 * Get manual dealer cash incentives
 * @param state
 */
export function getAppliedIncentivesForDealerCash(state: any) {
    const offerType = getCurrentOfferType(state);
    return getAppliedIncentivesWithFilter(state, offerType, isDealerCash);
}

/**
 * Get manual non-dealer cash incentives
 * @param state
 */
export function getAppliedIncentivesForNonDealerCash(state: any, offerType?: OfferType) {
    const offerT = offerType || getCurrentOfferType(state);
    return getAppliedIncentivesWithFilter(state, offerT, isNotDealerCash);
}

/**
 * Calculate dealer cash incentives total
 * @param state
 * @returns
 */
export function getAppliedIncentivesTotalForDealerCash(state: any, offerType?: OfferType, forUpqRequest = false) {
    const targetOfferType = offerType || getCurrentOfferType(state);
    return roundToTwoDecimal(
        getAppliedIncentivesWithFilter(state, targetOfferType, isDealerCash, forUpqRequest).reduce(
            (total, current) => total + current.amount,
            0
        )
    );
}

/**
 * Calculate non-dealer cash incentives total
 * @param state
 * @returns
 */
export function getAppliedIncentivesTotalForNonDealerCash(state: any, offerType?: OfferType) {
    const targetOfferType = offerType || getCurrentOfferType(state);
    return getAppliedIncentivesTotalWithFilter(state, targetOfferType, isNotDealerCash);
}

export const getFetchedProgramStandardCodes = (state: any, offerType: OfferType): number[] => {
    const standardIds = new Set(getCatalogNonConditionalIdsByOfferType(state, offerType));
    return (getManualIncentives(state, offerType) ?? [])
        .map((row) => Number(row.code))
        .filter((code) => !Number.isNaN(code) && standardIds.has(code));
};

export const areProgramStandardsFetched = (state: any, offerType: OfferType): boolean =>
    isGridIncentiveManagementEnabled(state, offerType) &&
    (hasRecordedNonConditionalSelection(state, offerType) || getFetchedProgramStandardCodes(state, offerType).length > 0);

export function getCarriedUpqRebateTotals(state: any, offerType: OfferType): { rebates: number; dealerCash: number } {
    const forUpqRequest = isGridIncentiveManagementEnabled(state, offerType);
    return {
        rebates: getAppliedIncentivesTotalWithFilter(state, offerType, isNotDealerCash, forUpqRequest),
        dealerCash: getAppliedIncentivesTotalForDealerCash(state, offerType, forUpqRequest)
    };
}

export const getExcludedStandardPredicate = (state: any, offerType: OfferType): ((incentive: ManualIncentiveEntry) => boolean) => {
    const standardIds = new Set<number>([
        ...getCatalogNonConditionalIdsByOfferType(state, offerType),
        ...getNonConditionalIncentiveFromTerm(state, getOfferDetails(state, offerType).term).map((incentive) =>
            parseIncentiveId(incentive.id)
        )
    ]);
    return (incentive) =>
        isProgramStandardIncentive(incentive) ||
        (Boolean(incentive.isStandardIncentive) && standardIds.has(resolveIncentiveNumericId(incentive) as number));
};

export function getAppliedIncentivesTotalForNonDealerCashWithoutStandards(state: any, offerType: OfferType) {
    const isExcludedStandard = getExcludedStandardPredicate(state, offerType);
    return getAppliedIncentivesTotalWithFilter(
        state,
        offerType,
        (incentive) => isNotDealerCash(incentive) && !isExcludedStandard(incentive)
    );
}

const isPricedWithoutOverrides = (incentive: ManualIncentiveEntry): boolean =>
    isNotDealerCash(incentive) && !isManualRebateForRequest(incentive);

const hasAppliedIncentiveIds = (term?: { appliedIncentiveIds?: number[] }): boolean => {
    const appliedIds = term?.appliedIncentiveIds;
    return Array.isArray(appliedIds) && appliedIds.length > 0;
};

const resolveTermAppliedIncentiveAmount = (amount: number | undefined, getComputedTotal: () => number): number =>
    amount == null || amount === 0 ? getComputedTotal() : amount;

/**
 * Applied incentives total for display. Prefers the amount the payment response applied to the
 * selected term, falling back to the locally computed non-dealer-cash total when the term reports no
 * applied set or reports an amount that contradicts the set it reports.
 *
 * The fallback sums the applied list as-is. It must not deduplicate by incentive code: a dealer may
 * enter several manual rows that reuse one program code with different amounts, so equal codes do
 * not mean equal rows (DE761820).
 *
 * @param state
 * @param offerType
 * @returns
 */
export function getAppliedIncentivesTotalForDisplay(state: any, offerType?: OfferType): number {
    const selectedOfferType = offerType || getCurrentOfferType(state);
    if (!isProgramIncentivesRestorationEnabled(state)) {
        return getAppliedIncentivesTotalForNonDealerCash(state, selectedOfferType);
    }
    const getComputedTotal = () => getAppliedIncentivesTotalForNonDealerCash(state, selectedOfferType);
    // The ledger gate is not `SinceLoad`: a deal that loaded on /program-quotes and then switched to
    // /user-program-quotes still has to read the bag, or the row and the request disagree.
    if (getIsUserProgramQuotesSinceLoad(state, selectedOfferType) || isUpqRebateLedgerActive(state, selectedOfferType)) {
        return getComputedTotal();
    }
    const term = getSelectedTermByOfferType(state, selectedOfferType) as
        | { appliedIncentiveAmount?: number; appliedIncentiveIds?: number[] }
        | undefined;
    const getPricedTotal = () =>
        shouldHonorDealerStandardExclusion(state, selectedOfferType) && !getHonorOverrides(state, selectedOfferType)
            ? getAppliedIncentivesTotalWithFilter(state, selectedOfferType, isPricedWithoutOverrides)
            : getComputedTotal();
    if (!hasAppliedIncentiveIds(term)) {
        return getPricedTotal();
    }
    return resolveTermAppliedIncentiveAmount(term?.appliedIncentiveAmount, getPricedTotal);
}

export function getSelectedNonConditionalIncentivesToDisplay(state: any, offerType?: OfferType): any[] {
    const selectedOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, selectedOfferType);
    const term = offerDetails?.term;
    const appliedNonConditionals =
        selectedOfferType === CASH
            ? getCashAppliedNonConditionalsIncentives(getCashOfferSource(state))
            : getNonConditionalAppliedIncentiveFromTerm(state, term);

    const nonConditionalIncentives =
        selectedOfferType === CASH
            ? getCashNonConditionalIncentives(getCashOfferSource(state))
            : getNonConditionalIncentivesToDisplayForTermId(state, term, offerType);

    const restorationEnabled =
        isProgramIncentivesRestorationEnabled(state) && !shouldHonorDealerStandardExclusion(state, selectedOfferType);

    if (restorationEnabled && selectedOfferType !== CASH) {
        const termNonConditionals = getNonConditionalIncentiveFromTerm(state, term);
        const compatibleIds = getCompatibleIncentiveIdsForTerm(state, term);
        const reportedAppliedIds = (getTerm(state, term) as { appliedIncentiveIds?: number[] } | undefined)?.appliedIncentiveIds;
        const reportedNoneApplied =
            isAppliedIncentiveDisplayFixesEnabled(state) && Array.isArray(reportedAppliedIds) && reportedAppliedIds.length === 0;
        if (!termNonConditionals?.length && compatibleIds && !reportedNoneApplied) {
            return nonConditionalIncentives;
        }
        const appliedIncentiveIds = getAppliedIncentiveIdsForSelectedTerm(state, selectedOfferType);
        if (isEmpty(appliedIncentiveIds)) return [];
        return (nonConditionalIncentives as OfferReduxBaseState[]).filter((incentive) =>
            appliedIncentiveIds.includes(incentive.pdId)
        );
    }

    if (restorationEnabled || !appliedNonConditionals?.length) {
        return nonConditionalIncentives;
    }

    return nonConditionalIncentives.filter(({ id }) => appliedNonConditionals.indexOf(id) >= 0);
}

/**
 * Sums rows for an itemized display. `transformToBaseIncentive` deletes a nullish `amount` from the
 * row outright, so a bag row carrying no amount reaches these lists without the property at all and
 * an uncoerced sum yields NaN (DE761820). Rounds for parity with `getAppliedIncentivesTotalWithFilter`.
 *
 * @param rows
 * @returns
 */
const sumDisplayedIncentiveAmounts = (rows: ManualIncentiveEntry[]): number =>
    roundToTwoDecimal(rows.reduce((total, current) => total + (Number(current.amount) || 0), 0));

/**
 * Resolves what a row is actually worth on the selected term.
 *
 * A row's money is not always on `amount`: `transformToBaseIncentive` deletes a nullish one, and a
 * program row can carry its value in `termRebates.incentiveTermRange` instead. The running totals
 * already resolve both through `helpers.transformIncentiveAmount`, so an itemized display has to use
 * the same rule or it disagrees with its own total (DE761820).
 *
 * @param state
 * @param offerType
 * @param row
 * @returns
 */
const resolveDisplayedAmount = (state: any, offerType: OfferType | undefined, row: ManualIncentiveEntry): number => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    return resolveAmountForTerm(
        getTermMonthByOfferTypeFromOffer(state.offer, resolvedOfferType) as number,
        row,
        isGridIncentiveManagementEnabled(state, resolvedOfferType)
    );
};

/**
 * Projects rows for an itemized display: resolves each amount, then drops the rows worth nothing.
 *
 * A bag row carrying no `amount` used to be absorbed into the program row sharing its code; removing
 * that collapse left it rendering as an unfunded `- $0` line (DE761820). Filtering on the raw
 * property alone would instead hide a row whose money lives in `termRebates` while the headline total
 * still counted it, so the amount is resolved onto the row first — the list, its total and
 * `getAppliedIncentivesTotalForNonDealerCash` then agree by construction.
 *
 * Gated: with restoration off the list is returned untouched.
 *
 * @param state
 * @param offerType
 * @param rows
 * @returns
 */
const isAppliedOnSelectedTerm = (state: any, offerType: OfferType | undefined, row: ManualIncentiveEntry): boolean => {
    const termId = getOfferDetails(state, offerType || getCurrentOfferType(state))?.term;
    const id = resolveIncentiveNumericId(row);
    return id != null && !isUserCustomIncentive(row) && Boolean(getGatedAppliedIdsForTerm(state, termId)?.has(id));
};

const withoutZeroAmountRows = (
    state: any,
    offerType: OfferType | undefined,
    rows: ManualIncentiveEntry[]
): ManualIncentiveEntry[] =>
    isProgramIncentivesRestorationEnabled(state)
        ? reconcileWithPricedTotal(
              state,
              offerType,
              rows
                  .map((row) => ({ ...row, amount: resolveDisplayedAmount(state, offerType, row) }))
                  .filter((row) => row.amount !== 0 || isAppliedOnSelectedTerm(state, offerType, row))
          )
        : rows;

/**
 * Drops rows the deal was never priced with, so the itemized list agrees with the payment.
 *
 * A term can report an applied set and a total that contradict each other — `appliedIncentiveIds`
 * naming two programs while `appliedIncentiveAmount` covers one. That is the shape DE761820 describes.
 * Re-deriving a per-row amount for every named id then overstates the deal: measured on the lease in
 * ShopperPlatform's `tax-payment-with-incentives`, rows resolve to 500 + 1250 while the term reports
 * 1250, and `capCostReduction` of 4168 decomposes exactly as `netCashDown` 2918 + 1250 — so 1250 is
 * the money actually in the deal.
 *
 * `getAppliedIncentivesTotalForDisplay` already trusts the term's figure, so without this the headline
 * total and the rows beneath it disagree.
 *
 * Only an exact reconciliation is applied: a single row whose removal makes the rest sum to the priced
 * total is dropped. Anything more ambiguous is left alone rather than guessed at, since dropping a row
 * that carries real money is worse than listing one that does not.
 *
 * And the total is only trusted when the payment math backs it. DE761820's own repro deal is the
 * mirror image of the case above — `appliedIncentiveAmount` 500 against a `capReductionRebate` of 1500,
 * the applied figure being the stale one. Reconciling against a too-low total would hide a real
 * incentive, which is the worse defect, so `isPricedTotalCorroborated` requires the cap cost reduction
 * to decompose as cash down plus the applied amount before any row is dropped.
 */
type PricedTerm = {
    appliedIncentiveAmount?: number;
    appliedIncentiveIds?: number[];
    capCostReduction?: number;
    netCashDown?: number;
};

/**
 * True when the term's cap cost reduction decomposes as cash down plus the applied incentive amount.
 *
 * This is the cross-check that makes the applied amount safe to reconcile against: on the lease
 * measured here, `capCostReduction` 4168 is `netCashDown` 2918 plus 1250. A term reporting a stale,
 * too-low applied amount fails it, and the list is then left alone.
 *
 * Terms that report no cap cost reduction — finance, or a lease with none — carry nothing to
 * corroborate against, so they are not reconciled either.
 */
const isPricedTotalCorroborated = (term: PricedTerm | undefined, priced: number): boolean => {
    const capCostReduction = term?.capCostReduction;
    const netCashDown = term?.netCashDown;
    if (capCostReduction == null || netCashDown == null) {
        return false;
    }
    return roundToTwoDecimal(capCostReduction - netCashDown) === roundToTwoDecimal(priced);
};

const reconcileWithPricedTotal = (
    state: any,
    offerType: OfferType | undefined,
    rows: ManualIncentiveEntry[]
): ManualIncentiveEntry[] => {
    const term = getSelectedTermByOfferType(state, offerType || getCurrentOfferType(state)) as PricedTerm | undefined;
    const priced = term?.appliedIncentiveAmount;
    if (!hasAppliedIncentiveIds(term) || priced == null || priced === 0 || rows.length < 2) {
        return rows;
    }
    if (!isPricedTotalCorroborated(term, priced)) {
        return rows;
    }
    const listed = sumDisplayedIncentiveAmounts(rows);
    if (listed === priced) {
        return rows;
    }
    const phantomIndex = rows.findIndex((row) => sumDisplayedIncentiveAmounts(rows.filter((other) => other !== row)) === priced);
    return phantomIndex === -1 ? rows : rows.filter((_row, index) => index !== phantomIndex);
};

export const getAppliedConditionalIncentives = (state: any, offerType?: OfferType) => {
    const appliedIncentives = getAppliedIncentivesForNonDealerCash(state, offerType);
    const conditionalIncentives = appliedIncentives.filter(({ isStandardIncentive }) => !isStandardIncentive);
    return conditionalIncentives;
};

export const getAppliedConditionalIncentivesAmount = (state: any, offerType?: OfferType) =>
    sumDisplayedIncentiveAmounts(getAppliedConditionalIncentives(state, offerType));

export const getAppliedNonconditionalIncentives = (state: any, offerType?: OfferType) => {
    const appliedIncentives = getAppliedIncentivesForNonDealerCash(state, offerType);
    const conditionalIncentives = appliedIncentives.filter(({ isStandardIncentive }) => isStandardIncentive);
    return conditionalIncentives;
};

export const getAppliedNonconditionalIncentivesAmount = (state: any, offerType?: OfferType) =>
    sumDisplayedIncentiveAmounts(getAppliedNonconditionalIncentives(state, offerType));

/**
 * Applied conditional incentives for an itemized display.
 *
 * This is the shared display projection: Manager View, the Shopper Platform deal summary (injected
 * through `IAdditionalSelectors`) and the printed PDF all read it, so a row cannot render in one
 * surface and be missing from another.
 *
 * It deliberately does not deduplicate. Equal incentive codes do not mean equal rows — a dealer can
 * enter several manual rows reusing one program code at different amounts, and folding them together
 * silently drops money (DE761820).
 *
 * @param state
 * @param offerType
 * @returns
 */
export const getAppliedConditionalIncentivesForDisplay = (state: any, offerType?: OfferType) =>
    withoutZeroAmountRows(state, offerType, getAppliedConditionalIncentives(state, offerType));

/**
 * Total of `getAppliedConditionalIncentivesForDisplay`, so a rendered list and its total cannot
 * disagree.
 * @param state
 * @param offerType
 * @returns
 */
export const getAppliedConditionalIncentivesAmountForDisplay = (state: any, offerType?: OfferType) =>
    sumDisplayedIncentiveAmounts(getAppliedConditionalIncentivesForDisplay(state, offerType));

/**
 * Applied non-conditional incentives for an itemized display. See
 * `getAppliedConditionalIncentivesForDisplay` for why this does not deduplicate.
 * @param state
 * @param offerType
 * @returns
 */
export const getAppliedNonconditionalIncentivesForDisplay = (state: any, offerType?: OfferType) =>
    withoutZeroAmountRows(state, offerType, getAppliedNonconditionalIncentives(state, offerType));

/**
 * Total of `getAppliedNonconditionalIncentivesForDisplay`.
 * @param state
 * @param offerType
 * @returns
 */
export const getAppliedNonconditionalIncentivesAmountForDisplay = (state: any, offerType?: OfferType) =>
    sumDisplayedIncentiveAmounts(getAppliedNonconditionalIncentivesForDisplay(state, offerType));

export function getNonConditionalIncentives(state: any) {
    const offerType = getCurrentOfferType(state);
    return getNonConditionalsByOfferType(state, offerType);
}

export function getNonConditionalTotal(state: any) {
    const offerType = getCurrentOfferType(state);
    return getNonConditionalTotalByOfferType(state, offerType);
}

export function getNonConditionalsByOfferType(state: any, offerType: any) {
    const offerDetails = getOfferDetails(state, offerType);
    let nonConditionals: any[];
    if (offerType === 'cash') {
        nonConditionals = getCashNonConditionalIncentives(state.offer);
    } else {
        nonConditionals = getNonConditionalIncentivesToDisplayForTermId(state, offerDetails.term);
    }

    const appliedIds = getGatedAppliedIdsForTerm(state, offerDetails.term);
    return appliedIds
        ? nonConditionals.filter(
              (incentive) => incentive?.amount > 0 || appliedIds.has(resolveIncentiveNumericId(incentive) as number)
          )
        : helpers.getIncentivesWithAmount(nonConditionals);
}

export function getNonConditionalTotalByOfferType(state: any, offerType: any) {
    const nonConditionals = getNonConditionalsByOfferType(state, offerType);
    return helpers.findIncentiveTotal(nonConditionals);
}

export function getSelectedConditionalTotal(state: any) {
    const offerType = getCurrentOfferType(state);
    return getSelectedConditionalTotalByOfferType(state, offerType);
}

export function getSelectedConditionalTotalByOfferType(state: any, offerType: any) {
    const offerDetails = getOfferDetails(state, offerType);
    let conditionals;
    let selectedConditionalIds;
    if (offerType === 'cash') {
        conditionals = getCashConditionalIncentives(state.offer);
        selectedConditionalIds = getCashSelectedIncentives(state.offer);
    } else {
        selectedConditionalIds = getSelectedIncentivesByOfferType(state, offerType);
        conditionals = getConditionalIncentivesToDisplayForTermId(state, offerDetails.term);
    }

    const selectedIncentives = conditionals.filter((incentive) => {
        return selectedConditionalIds.includes(incentive.id);
    });

    return helpers.findIncentiveTotal(selectedIncentives);
}

/**
 * Gets the incentives total amount penciled while credit decision enabled (generic calcs)
 */
export const getIncentivesTotalOverride = (state: any): number | undefined => {
    const offerType = getCurrentOfferType(state);
    return getIncentivesTotalOverrideByOfferType(state, offerType);
};

export function getAllIncentiveTotal(state: any): number {
    // Note: for conditional, only take the selected
    return getIsUserProgramQuotes(state)
        ? getIncentivesTotalOverride(state) ?? 0
        : getNonConditionalTotal(state) + getSelectedConditionalTotal(state);
}

export function getAllIncentiveTotalByOfferType(state: any, offerType: OfferType): number {
    return (
        (getIsUserProgramQuotes(state)
            ? getIncentivesTotalOverrideByOfferType(state, offerType)
            : getNonConditionalTotalByOfferType(state, offerType) + getSelectedConditionalTotalByOfferType(state, offerType)) ?? 0
    );
}

export function getConditionalTotal(state: any, offerType?: OfferType): number {
    const type = offerType ?? getCurrentOfferType(state);
    return (
        (getIsUserProgramQuotes(state)
            ? getIncentivesTotalOverrideByOfferType(state, type)
            : getConditionalTotalByOfferType(state, type)) ?? 0
    );
}

export function getConditionalTotalByOfferType(state: any, offerType: any) {
    const offerDetails = getOfferDetails(state, offerType);
    let conditionals;
    if (offerType === 'cash') {
        conditionals = getCashConditionalIncentives(state.offer);
    } else {
        if (getIsUserProgramQuotes(state)) {
            return getIncentivesTotalOverrideByOfferType(state, offerType);
        }
        conditionals = getConditionalIncentivesToDisplayForTermId(state, offerDetails.term);
    }

    return helpers.findIncentiveTotal(conditionals);
}

export const getSelectedConditionalIncentiveNameAndId = (state: any) => {
    return getSelectedConditionalIncentivesToDisplay(state).map((incentive: any) => ({
        id: `${incentive.pdId}`,
        name: incentive.name
    }));
};

export const getManualIncentives = (state: any, offerType?: OfferType): ManualIncentiveEntry[] | undefined => {
    const targetOfferType = offerType || getCurrentOfferType(state);
    return getManualIncentivesByOfferType(state, targetOfferType);
};

export function getSelectedIncentivesBySelectedOfferType(state: any) {
    const offerType = getCurrentOfferType(state);
    return getSelectedIncentivesByOfferType(state, offerType);
}

export const getCurrentSelectedIncentives = (state: any) => {
    const offerType = getCurrentOfferType(state);
    const selectedIncentives = getSelectedIncentivesByOfferType(state, offerType);
    return selectedIncentives;
};

export function formatSelectedIncentiveByOfferType(state: any, offerType: any) {
    const selectedIncentives = getSelectedIncentivesByOfferType(state, offerType);
    if (isEmpty(selectedIncentives)) {
        return [];
    }
    // Strip prefix to get numeric ID. Must replace 'nonconditional_' first,
    // otherwise 'conditional_' matches the tail of 'nonconditional_' and leaves 'non' behind.
    const formatedSelectedIncentives = selectedIncentives.map((incentive: string) => {
        return incentive.replace(`nonconditional_${offerType}_`, '').replace(`conditional_${offerType}_`, '');
    });
    return formatedSelectedIncentives;
}

export function getMappedIdsForSelectedIncentivesBySelectedOfferType(state: any) {
    const offerType = getCurrentOfferType(state);
    return getMappedIdsForSelectedIncentivesByOfferType(state, offerType);
}

export function getMappedIdsForSelectedIncentivesByOfferType(state: any, offerType: any) {
    const incentives = getIncentives(state);
    return helpers.getMappedIncentives(incentives.mappings, getSelectedIncentivesByOfferType(state, offerType));
}

/**
 * Memoized numeric ids of the selected incentives for the current offer type, derived from
 * penciledInputs. Stable reference while the selection + mappings are unchanged, so it is safe
 * for `useSelector` consumers that don't pass an equality fn.
 */
export const getSelectedIncentiveIdsFromPenciledInputs = createSelector(
    [
        (state: any) => getSelectedIncentivesByOfferType(state, getCurrentOfferType(state)),
        (state: any) => getIncentives(state).mappings
    ],
    (selectedIncentives, mappings) => helpers.getMappedIncentives(mappings, selectedIncentives)
);

const isValidIncentiveId = (id: unknown): id is number => typeof id === 'number' && !Number.isNaN(id);

const toValidIncentiveIds = (ids: unknown): number[] => Array.from(new Set(((ids as unknown[]) ?? []).filter(isValidIncentiveId)));

const getCompatibleIdsWhenHonoringDealerStandardExclusion = (state: any, offerType: OfferType): number[] | undefined => {
    if (!shouldHonorDealerStandardExclusion(state, offerType)) {
        return undefined;
    }
    const term = getSelectedTerm(state, offerType) as {
        compatibleIncentiveIds?: number[];
        incentivesContext?: { compatibleIncentiveIds?: number[] };
    };
    const direct = term.compatibleIncentiveIds;
    return Array.isArray(direct) && direct.length > 0 ? direct : term.incentivesContext?.compatibleIncentiveIds;
};

const keepCompatibleIds = (ids: number[], compatibleIds: number[] | undefined): number[] =>
    Array.isArray(compatibleIds) && compatibleIds.length > 0 ? ids.filter((id) => compatibleIds.includes(id)) : ids;

export const getAppliedIncentiveIdsForSelectedTerm = createSelector(
    [
        (state: any, offerType?: OfferType) => getSelectedTerm(state, offerType || getCurrentOfferType(state))?.appliedIncentiveIds,
        (state: any, offerType?: OfferType) => getOfferDetails(state, offerType || getCurrentOfferType(state))?.appliedIncentiveIds,
        (state: any) => isProgramIncentivesRestorationEnabled(state),
        (state: any, offerType?: OfferType) => isCashOfferType(state, offerType),
        (state: any, offerType?: OfferType) => hasRecordedIncentiveSelection(state, offerType),
        (state: any, offerType?: OfferType) =>
            getCompatibleIdsWhenHonoringDealerStandardExclusion(state, offerType || getCurrentOfferType(state))
    ],
    (termAppliedIds, penciledAppliedIds, restorationEnabled, isCash, hasRecordedSelection, compatibleIds) => {
        const termIds = toValidIncentiveIds(termAppliedIds);
        if (termIds.length > 0 || !restorationEnabled || isCash) {
            return termIds;
        }
        // The penciled fallback covers a term that has not reported an applied set yet. Once the user
        // has recorded a selection, an empty term set means "nothing applied" and must not fall back,
        // or an unchecked incentive keeps rendering as applied.
        return hasRecordedSelection ? termIds : keepCompatibleIds(toValidIncentiveIds(penciledAppliedIds), compatibleIds);
    }
);

export function getMappedIdsForNonConditionalIncentivesBySelectedOfferType(state: any) {
    const offerType = getCurrentOfferType(state);
    return getMappedIdsForNonConditionalIncentivesByOfferType(state, offerType);
}

export function getMappedIdsForNonConditionalIncentivesByOfferType(state: any, offerType: OfferType) {
    const termId = getTermIdForOfferType(state.offer, offerType);
    const incentives = getIncentives(state);
    return helpers.getMappedIncentives(incentives.mappings, getNonConditionalAppliedIncentiveFromTerm(state, termId));
}

export function getAvailableNonConditionalIncentiveFromTerm(state: any, termId: any): string[] {
    return getNonConditionalIncentiveFromTerm(state, termId).map((incentive) => incentive.id);
}

export function getMappedIdsForAvailableNonConditionalIncentivesByOfferType(state: any, offerType: OfferType) {
    const incentives = getIncentives(state);
    const availableIds =
        offerType === CASH
            ? getCashNonConditionalsIncentives(state.offer).map((incentive) => incentive.id)
            : getAvailableNonConditionalIncentiveFromTerm(state, getTermIdForOfferType(state.offer, offerType));
    return helpers.getMappedIncentives(incentives.mappings, availableIds);
}

export function getNonConditionalIncentiveFromOfferTerm(offer: Offer, termId: string): TermsIncentives | SlimTermsIncentive[] {
    const termById = getTermFromOffer(offer, termId);
    const incentives = termById.incentives || ({} as ITermsIncentives);
    return incentives.nonConditionals || [];
}

export function getNonConditionalAppliedIncentiveFromOfferTerm(offer: any, termId: any): string[] {
    const termById = getTermFromOffer(offer, termId);
    const appliedIncentives = termById.appliedIncentives || ({} as TermsAppliedIncentives);
    return appliedIncentives.nonConditionals || [];
}

export function getNonConditionalIncentivesForTermIdFromOffer(offer: Offer, termId: any): OfferIncentiveWithAmount[] {
    const termIncentives = getNonConditionalIncentiveFromOfferTerm(offer, termId);
    const termAppliedIds = getNonConditionalAppliedIncentiveFromOfferTerm(offer, termId);
    const nonConditionalIncentivesToDisplay: OfferIncentiveWithAmount[] = [];

    termIncentives.forEach((incentive) => {
        if (incentive.id && termAppliedIds.indexOf(incentive.id) >= 0) {
            const incentiveWithAmount = { ...getIncentiveById(offer, incentive.id), ...{ amount: incentive.amount } };
            nonConditionalIncentivesToDisplay.push(incentiveWithAmount);
        }
    });
    return nonConditionalIncentivesToDisplay;
}

export function getNonConditionalTotalByOfferTypeFromOffer(offer: Offer, offerType: any): number {
    if (!offer.penciledInputs) return 0;
    const offerDetails = offer.penciledInputs.byOfferType![
        offerType as keyof typeof offer.penciledInputs.byOfferType
    ] as IPencilOfferInputs;
    let nonConditionals: SlimTermsIncentive[] | OfferIncentiveWithAmount[] = [];
    if (offerType === CASH) {
        nonConditionals = getCashNonConditionalIncentives(offer);
    } else {
        nonConditionals = getNonConditionalIncentivesForTermIdFromOffer(offer, offerDetails?.term);
    }
    return helpers.findIncentiveTotal(nonConditionals);
}

export function getConditionalIncentivesForTermIdFromOffer(offer: Offer, termId: any) {
    const conditionalIncentives = getConditionalIncentiveFromOfferTerm(offer, termId) || [];
    const conditionalIncentivesToDisplay: OfferIncentiveWithAmount[] = [];

    if (conditionalIncentives.length > 0) {
        conditionalIncentives.forEach((condIncentive) => {
            if (condIncentive.id) {
                const incentiveWithAmount = { ...getIncentiveById(offer, condIncentive.id), ...{ amount: condIncentive.amount } };
                conditionalIncentivesToDisplay.push(incentiveWithAmount);
            }
        });
    }

    return conditionalIncentivesToDisplay;
}

export function getSelectedIncentivesByOfferTypeFromOffer(offer: any, offerType: any) {
    const userInput = offer.penciledInputs.byOfferType[offerType];
    if (isEmpty(userInput)) {
        return [];
    }
    return userInput.selectedIncentives;
}

export const getSelectedConditionalTotalByOfferTypeFromOffer = (offer: Offer, offerType: OfferType): number => {
    const offerDetails = offer.penciledInputs?.byOfferType![offerType];
    const selectedConditionalIds = getSelectedIncentivesByOfferTypeFromOffer(offer, offerType);
    let conditionals: SlimTermsIncentive[] | OfferIncentiveWithAmount[] = [];

    if (offerType === CASH) {
        conditionals = getCashConditionalIncentives(offer);
    } else {
        conditionals = getConditionalIncentivesForTermIdFromOffer(offer, offerDetails?.term);
    }

    const selectedIncentives = conditionals.filter((incentive) => {
        return selectedConditionalIds?.includes(incentive.id);
    });

    return helpers.findIncentiveTotal(selectedIncentives);
};

export const findAllIncentiveTotalFromOffer = (offer: any, offerType: any) => {
    return (
        getNonConditionalTotalByOfferTypeFromOffer(offer, offerType) +
        getSelectedConditionalTotalByOfferTypeFromOffer(offer, offerType)
    );
};
/*
 * The above are for adding the incentive total to the offer reducer based on offer.
 */

export function doIncentivesExist(state: any) {
    const offerType = getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, offerType);
    const financeOrLeaseConditionals = getConditionalIncentivesToDisplayForTermId(state, offerDetails.term);
    const cashConditionals = getCashConditionalIncentives(state.offer);

    return !!(financeOrLeaseConditionals.length > 0 || cashConditionals.length > 0);
}

export const getIncentiveIds = (selected: string[]): number[] => {
    if (isEmpty(selected)) {
        return [];
    }

    return selected.filter(Boolean).map(parseIncentiveId);
};
